import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/shared/auth/AuthProvider";
import { publicWebCopyForLang } from "@/shared/i18n/public-web-copy";
import { useMarketingExperience } from "@/shared/i18n/use-product-locale";
import { safeAppRouteTarget } from "@/shared/navigation/app-route-memory";
import { loadFirstRunState, onFirstRunChanged } from "@/shared/onboarding/first-run";
import { appEntryDestination } from "./app-entry-policy.mjs";

export function RequireAuth({ children }: { children: ReactNode }) {
  const { signedIn, booting, contactVerificationRequired } = useAuth();
  const location = useLocation();
  const experience = useMarketingExperience();
  const copy = useMemo(() => publicWebCopyForLang(experience.lang), [experience.lang]);
  const [completed, setCompleted] = useState(() => loadFirstRunState().completed);

  useEffect(
    () => onFirstRunChanged(() => setCompleted(loadFirstRunState().completed)),
    [],
  );

  if (booting) {
    return (
      <div style={{ padding: "2rem", color: "var(--tb-fg-muted)" }} role="status">
        {copy("auth.route.loading")}
      </div>
    );
  }

  const destination = appEntryDestination({
    signedIn,
    contactVerificationRequired,
    completed,
    pathname: location.pathname,
  });
  if (destination) {
    const from = safeAppRouteTarget(
      `${location.pathname}${location.search}${location.hash}`,
      "/app/brief",
    );
    return <Navigate to={destination} replace state={{ from, contactVerificationRequired }} />;
  }

  return children;
}
